import * as THREE from 'three';
import { CONFIG } from '../app/config.js';

export class TrackBuilder {
  constructor(scene) {
    this.scene = scene;
    this.segments = [];
    this.decorations = [];
    this.trackGroup = new THREE.Group();

    // Reusable shared materials and geometries (allocated once)
    this.trackMaterial = new THREE.MeshStandardMaterial({
      color: 0x131b2e,
      roughness: 0.7,
      metalness: 0.2,
    });

    this.laneMarkerMaterial = new THREE.MeshBasicMaterial({
      color: 0x42e8e0,
      transparent: true,
      opacity: 0.75,
    });

    this.curbMaterial = new THREE.MeshStandardMaterial({
      color: 0x2b3852,
      roughness: 0.5,
      metalness: 0.4,
    });

    this.neonTrimMaterial = new THREE.MeshBasicMaterial({
      color: 0xf05bd5,
    });

    this.buildingMaterial = new THREE.MeshStandardMaterial({
      color: 0x0f1626,
      roughness: 0.9,
      metalness: 0.1,
    });

    this.buildingWindowMaterial = new THREE.MeshBasicMaterial({
      color: 0x243555,
    });
  }

  build() {
    this.scene.add(this.trackGroup);

    const segLength = CONFIG.WORLD.TRACK_SEGMENT_LENGTH;
    const segCount = CONFIG.WORLD.TRACK_SEGMENT_COUNT;
    const totalSpan = segLength * segCount;

    // Build reusable modular track segments
    for (let i = 0; i < segCount; i++) {
      const segmentGroup = this.createSegmentMesh(segLength);
      segmentGroup.position.z = -i * segLength;
      this.trackGroup.add(segmentGroup);
      this.segments.push(segmentGroup);
    }

    // Build distant futuristic skyline backdrop
    this.buildSkyline(totalSpan);
  }

  createSegmentMesh(length) {
    const group = new THREE.Group();
    const trackWidth = CONFIG.WORLD.TRACK_WIDTH;

    // Main road deck
    const roadGeo = new THREE.BoxGeometry(trackWidth, 0.4, length);
    const roadMesh = new THREE.Mesh(roadGeo, this.trackMaterial);
    roadMesh.position.y = -0.2;
    group.add(roadMesh);

    // Left and Right curbs with neon glow strips
    const curbGeo = new THREE.BoxGeometry(0.35, 0.6, length);
    const curbLeft = new THREE.Mesh(curbGeo, this.curbMaterial);
    curbLeft.position.set(-trackWidth / 2 - 0.175, 0.1, 0);
    group.add(curbLeft);

    const curbRight = new THREE.Mesh(curbGeo, this.curbMaterial);
    curbRight.position.set(trackWidth / 2 + 0.175, 0.1, 0);
    group.add(curbRight);

    const trimGeo = new THREE.BoxGeometry(0.08, 0.12, length);
    const trimLeft = new THREE.Mesh(trimGeo, this.neonTrimMaterial);
    trimLeft.position.set(-trackWidth / 2, 0.35, 0);
    group.add(trimLeft);

    const trimRight = new THREE.Mesh(trimGeo, this.neonTrimMaterial);
    trimRight.position.set(trackWidth / 2, 0.35, 0);
    group.add(trimRight);

    // Two dashed lane dividers separating the 3 lanes
    // Lane coords are: [-2.4, 0, 2.4] -> dividers at x = -1.2 and x = +1.2
    const dividerX = [
      (CONFIG.LANES.COORDS[0] + CONFIG.LANES.COORDS[1]) / 2,
      (CONFIG.LANES.COORDS[1] + CONFIG.LANES.COORDS[2]) / 2,
    ];

    const dashCount = Math.floor(length / 4);
    const dashGeo = new THREE.PlaneGeometry(0.12, 1.8);
    dashGeo.rotateX(-Math.PI / 2);

    for (let d = 0; d < dashCount; d++) {
      const zOffset = -length / 2 + d * 4 + 2;
      for (const x of dividerX) {
        const dash = new THREE.Mesh(dashGeo, this.laneMarkerMaterial);
        dash.position.set(x, 0.01, zOffset);
        group.add(dash);
      }
    }

    return group;
  }

  buildSkyline(totalSpan) {
    const buildingGeo = new THREE.BoxGeometry(1, 1, 1);
    const buildingCount = 36;

    for (let i = 0; i < buildingCount; i++) {
      const bMesh = new THREE.Mesh(buildingGeo, this.buildingMaterial);
      const isLeft = i % 2 === 0;
      const xDist = (isLeft ? -1 : 1) * (18 + Math.random() * 30);
      const zPos = -Math.random() * totalSpan;
      const width = 8 + Math.random() * 12;
      const height = 25 + Math.random() * 50;
      const depth = 8 + Math.random() * 12;

      bMesh.scale.set(width, height, depth);
      bMesh.position.set(xDist, height / 2 - 10, zPos);
      this.trackGroup.add(bMesh);
      this.decorations.push(bMesh);
    }
  }

  update(worldSpeed, dt) {
    const dz = worldSpeed * dt;
    const segLength = CONFIG.WORLD.TRACK_SEGMENT_LENGTH;
    const segCount = CONFIG.WORLD.TRACK_SEGMENT_COUNT;
    const totalSpan = segLength * segCount;

    // Scroll track segments along +Z toward camera
    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i];
      seg.position.z += dz;

      // Wrap around when segment has completely moved past the camera view
      if (seg.position.z >= CONFIG.WORLD.RECYCLE_Z) {
        seg.position.z -= totalSpan;
      }
    }

    // Slowly scroll background scenery for parallax
    const decorationDz = dz * 0.3;
    for (let i = 0; i < this.decorations.length; i++) {
      const dec = this.decorations[i];
      dec.position.z += decorationDz;
      if (dec.position.z > 20) {
        dec.position.z -= totalSpan;
      }
    }
  }

  dispose() {
    this.trackMaterial.dispose();
    this.laneMarkerMaterial.dispose();
    this.curbMaterial.dispose();
    this.neonTrimMaterial.dispose();
    this.buildingMaterial.dispose();
    this.buildingWindowMaterial.dispose();

    if (this.trackGroup.parent) {
      this.trackGroup.parent.remove(this.trackGroup);
    }
  }
}
